import Backdrop from "@mui/material/Backdrop";
import { useEffect, useMemo, useState } from "react";
import ProgressCard from "../../common/components/molecules/ProgressCard/ProgressCard";
import useProgressLoader from "../../common/hooks/useProgressLoader";
import { FRAME_EVENT } from "../consts/Frame";
import EventManager from "../EventManager/EventManager";
import { getTargetIframe } from "../utils/utils";
import { getActiveStepIdx, getLoaderConfig } from "./LoaderManager.helper";
import isUndefined from "lodash/isUndefined";

const eventManager = EventManager.getInstance();

const TIME_STEP_DURATION = 100;
const DISMISS_TIME = 1000;
const PROGRESS_AUTO_INCREASE_THRESHOLD = 92;
const INITIAL_PROGRESS_VALUE = 10;
const MAX_PROGRESS_VALUE = 100;

/**
 * Manager Component to Manage global Loader
 * @returns
 */
export default function LoaderManager() {
    const [loaderState, setLoaderState] = useState({
        id: "",
        open: false,
    });

    // get loader config
    const { expectedTime, steps } = useMemo(
        () => getLoaderConfig(loaderState.id),
        [loaderState.id],
    );

    const { value, start, finish, reset } = useProgressLoader({
        totalTime: expectedTime,
        timeStep: TIME_STEP_DURATION,
        startVal: INITIAL_PROGRESS_VALUE,
        stopVal: PROGRESS_AUTO_INCREASE_THRESHOLD,
        maxVal: MAX_PROGRESS_VALUE,
        timingFunction: "easeInOut",
    });

    // get Active step details
    const activeStepIdx = useMemo(
        () => getActiveStepIdx(steps, value),
        [steps, value],
    );

    useEffect(() => {
        eventManager.subscribe((type, data) => {
            switch (type) {
                case FRAME_EVENT.SHOW_PROGRESS_LOADER: {
                    setLoaderState({
                        id: data?.id || "",
                        open: true,
                    });
                    return;
                }

                case FRAME_EVENT.RESET: {
                    // reset the progress bar, hide the loader
                    setLoaderState((prevState) => ({
                        ...prevState,
                        open: false,
                    }));
                    reset();
                    return;
                }

                case FRAME_EVENT.HIDE_PROGRESS_LOADER: {
                    finish();
                    setTimeout(
                        () => {
                            setLoaderState((prevState) => ({
                                ...prevState,
                                open: false,
                            }));
                            // reset the progress bar
                            reset();

                            const sourceIframe = getTargetIframe(
                                data?.scrollToIframe,
                            );
                            sourceIframe?.scrollIntoView({
                                block: "start",
                                behavior: "smooth",
                            });
                        },
                        isUndefined(data?.dismissTime)
                            ? DISMISS_TIME
                            : data?.dismissTime,
                    );
                    return;
                }
                default:
                    return;
            }
        });
    }, [setLoaderState, finish]);

    useEffect(() => {
        if (loaderState.open) {
            start();
        }
    }, [loaderState.open, start]);

    return (
        <Backdrop open={loaderState.open} sx={{ zIndex: 999999 }}>
            {loaderState.open && (
                <ProgressCard
                    value={value}
                    currentStep={activeStepIdx + 1}
                    totalStep={steps.length}
                    title={steps[activeStepIdx].message}
                    subTitle={steps[activeStepIdx].secondaryMessage}
                    image={steps[activeStepIdx].image}
                />
            )}
        </Backdrop>
    );
}
